import {
    Button,
    Container,
    FormControl,
    FormControlLabel,
    Grid2,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "components/common/Select";
import { Field, Form, Formik } from "formik";
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";

import { default as FilmReleaseSelect2 } from "components/global/FilmReleaseSelect";

import FilmReleaseAutocomplete from "components/common/FilmReleaseAutocomplete";

import ConfirmDialog from "components/common/ConfirmDialog";
import { SMALL_DEVICE_MEDIA_SIZE } from "components/common/constants";
import { ACTION_STATES } from "core/actions";
import log from "loglevel";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFnsV3";
import useMediaQuery from "@mui/material/useMediaQuery";
import {  LocalizationProvider } from "@mui/x-date-pickers";
import ContactDropdown from "components/common/Contacts";
import CompanySelect from "components/global/CompanySelect";
import FilmRatingSwitch from "components/global/FilmRatingSwitch";
import { get } from "lodash";
import "./styles.scss";

import { useTheme } from "@mui/material";
import RatingCategoryComponent from "components/global/RatingCategorySelect";
import { FILM_RELEASE_TYPES } from "core/dataConstants";
import {DATE_FORMAT_SHORT, formatDate, stringToDate} from "components/common/dateUtils";
import ResponsiveDatePicker from "components/common/form/ResponsiveDatePicker";

function FilmReleaseForm(props, ref) {
    const {
        addStatus,
        editStatus,
        editing,
        addFilmRelease,
        editFilmRelease,
        onMergeFilmRelease,
        onRequestAssociation,
        initValues,
        onAddFromExisting,
    } = props;

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [releaseToMerge, setReleaseToMerge] = useState(null);
    const [releaseToAssociate, setReleasetoAssociate] = useState(null);
    const [typeToChange, setTypeToChange] = useState(-1);
    const bagRef = useRef();
    let formChanged = false;

    useImperativeHandle(ref, () => ({
        submitForm() {
            log.debug(bagRef);
            bagRef.current.submitForm();
        },
    }));

    const handleConfirmDialogClose = (resetForm, submitForm) => {
        formChanged = false;
        if (submitForm) {
            submitForm();
        } else {
            setConfirmDialogOpen(false);
        }
    };

    function handleOnSubmit(values, { resetForm }) {
        log.debug("values", values);
        const titleType = typeof values.title;
        const body = {
            title: titleType === "string" ? values.title : values.title.label,
            filmReleaseTypeID: values.filmReleaseTypeID,
            companyID: values.companyID.value,
            filmRatingTypeID: values.filmRatingTypeID,
            ratingReason: values.ratingReason,
            ratingCategory: values.ratingCategory,
            ratingSubCategory: values.ratingSubCategory,
            releaseDate: values.releaseDate ? formatDate(values.releaseDate, DATE_FORMAT_SHORT) : null,

            ratingDate: values.ratingDate ? formatDate(values.ratingDate, DATE_FORMAT_SHORT) : null,
            filmId: titleType === "string" ? null : values.title.filmId,
            contactsIds: values.contacts.map((contact) => contact.id),
            id: initValues ? initValues.id : null,
        };
        log.debug("body", body);
        if (!editing) {
            body.id = null;
            addFilmRelease(body, resetForm);
        } else {
            log.debug(body, initValues);
            const currentTitle = get(initValues, "title.label", initValues.title);
            if (body.title !== currentTitle && !confirmDialogOpen) {
                return setConfirmDialogOpen(true);
            }

            setConfirmDialogOpen(false);
            editFilmRelease(body, resetForm);
        }
    }

    function onMergeTitle(value) {
        bagRef.current.resetForm();
        setReleaseToMerge(value);
    }

    function handleOnMerge() {
        onMergeFilmRelease(initValues, releaseToMerge);
    }

    function handleOnAssociate() {
        onRequestAssociation(initValues, releaseToAssociate);
    }

    function onAssociateRelease(value) {
        setReleasetoAssociate(value);
    }

    function handleCreateNewRelease() {
        onAddFromExisting(initValues, true);
        setTypeToChange(-1);
    }

    function handleOnTypeChange(e, setFieldValue) {
        log.debug("value", e);
        if (editing && typeToChange === -1) {
            setTypeToChange(e.target.value);
        }
        setFieldValue("filmReleaseTypeID", e.target.value);
    }

    function handleCloseChangeType() {
        setTypeToChange(0);
    }

    function handleCategoryChange(cat, subCat) {
        bagRef.current.setFieldValue("ratingCategory", cat);
        bagRef.current.setFieldValue("ratingSubCategory", subCat);
        log.debug(cat, subCat);
    }

    return (
        (<Formik
            innerRef={bagRef}
            enableReinitialize
            initialValues={{
                title: initValues && initValues.title.label,
                filmReleaseTypeID: initValues && initValues.filmReleaseTypeID,
                companyID: initValues && initValues.companyID,
                contacts: initValues ? initValues.contacts : [],
                filmReferenceId: initValues ? initValues.filmReferenceId : null,
                filmRatingTypeID: initValues && initValues.filmRatingTypeID,
                ratingReason: initValues && initValues.ratingReason,
                releaseDate: initValues && initValues.releaseDate ? stringToDate(initValues.releaseDate) : null,
                ratingDate: initValues && initValues.ratingDate ? stringToDate(initValues.ratingDate) : null,
                enteredDate:
                    initValues && initValues.enteredDate ? stringToDate(formatDate(initValues.enteredDate, DATE_FORMAT_SHORT)) :(new Date()),
                alternativeTitles: initValues ? initValues.alternativeTitles : [],
                ratingCategory: initValues ? initValues.ratingCategory : null,
                ratingSubCategory: initValues ? initValues.ratingSubCategory : null,
            }}
            validate={(values) => {
                if (editing && !formChanged) {
                    formChanged = true;
                }

                const requiredMsg = "This field is required";
                const errors = {};
                if (!values.title) errors.title = requiredMsg;
                if (!values.companyID) errors.companyID = requiredMsg;
                if (!values.filmReleaseTypeID) errors.filmReleaseTypeID = requiredMsg;
                return errors;
            }}
            onSubmit={handleOnSubmit}
        >
            {({ submitForm, resetForm, values }) => {
                return (
                    (<React.Fragment>
                        {(addStatus === ACTION_STATES.SEND || editStatus === ACTION_STATES.SEND) && (
                            <Backdrop style={{ color: "#fff", zIndex: 10 }} open={true}>
                                <CircularProgress color="inherit" />
                            </Backdrop>
                        )}
                        {confirmDialogOpen && (
                            <ConfirmDialog
                                open={confirmDialogOpen}
                                message="Are you sure that you want to rename the film release?"
                                confirmText="Yes"
                                closeText="Cancel"
                                onConfirm={() => handleConfirmDialogClose(resetForm, submitForm)}
                                onCancel={() => handleConfirmDialogClose(resetForm)}
                            />
                        )}
                        {typeToChange > 0 && (
                            <ConfirmDialog
                                open={typeToChange > 0}
                                message="Would you like to create a new release of an existing movie. Or just change its release type ? "
                                confirmText="New release"
                                closeText="Change type"
                                onConfirm={handleCreateNewRelease}
                                onCancel={handleCloseChangeType}
                            />
                        )}
                        <FormContainer>
                            <div style={{ padding: 30, paddingTop: 10, position: "relative" }}>
                                <Form>
                                    <Grid2 id="add-film-release-form" container spacing={1}>
                                        <Grid2
                                            container
                                            style={{ alignContent: "baseline", alignItems: "baseline" }}
                                            size={12}
                                        >
                                            <Grid2 container size={12}>
                                                <Grid2
                                                    className="field-label-wrapper"
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}>
                                                    <Typography variant={"subtitle2"} className="field-label">
                                                        Film Title
                                                    </Typography>
                                                </Grid2>
                                                <Grid2
                                                    className="field"
                                                    size={{
                                                        xs: 12,
                                                        md: 8
                                                    }}>
                                                    <Field name="title">
                                                        {({ field, form: { touched, errors, setFieldValue } }) => {
                                                            return (
                                                                <React.Fragment>
                                                                    {(field.value || !editing) && (
                                                                        <FilmReleaseAutocomplete
                                                                            style={{ width: "calc(100% - 150px" }}
                                                                            isForm
                                                                            onChange={(option) =>
                                                                                setFieldValue("title", option)
                                                                            }
                                                                            defaultValue={field.value}
                                                                        />
                                                                    )}
                                                                    {errors.title && touched.title && (
                                                                        <small className="error">{errors.title}</small>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        }}
                                                    </Field>
                                                </Grid2>
                                                <Grid2
                                                    className="field"
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}>
                                                    {editing && (
                                                        <Button
                                                            variant={"outlined"}
                                                            style={{ marginTop: 16 }}
                                                            onClick={handleCreateNewRelease}
                                                            color={"secondary"}
                                                        >
                                                            New Release
                                                        </Button>
                                                    )}
                                                </Grid2>
                                            </Grid2>
                                            <Grid2 container size={12}>
                                                <Grid2
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}></Grid2>
                                                <Grid2
                                                    className="field"
                                                    size={{
                                                        xs: 12,
                                                        md: 8
                                                    }}>
                                                    <Field name="filmReleaseTypeID">
                                                        {({ field, form: { touched, errors, setFieldValue } }) => {
                                                            // eslint-disable-next-line no-unused-vars
                                                            const { onChange, ...restField } = field;

                                                            return (
                                                                <FormControl
                                                                    component="fieldset"
                                                                    style={{ width: "100%" }}
                                                                >
                                                                    <RadioGroup
                                                                        style={{ flexDirection: "row" }}
                                                                        {...restField}
                                                                        value={field.value}
                                                                        onChange={(e) =>
                                                                            handleOnTypeChange(e, setFieldValue)
                                                                        }
                                                                    >
                                                                        {Object.keys(FILM_RELEASE_TYPES).map((key) => {
                                                                            const item = FILM_RELEASE_TYPES[key];
                                                                            return (
                                                                                <FormControlLabel
                                                                                    value={item.id}
                                                                                    key={item.id}
                                                                                    control={
                                                                                        <Radio
                                                                                            checked={
                                                                                                field.value === item.id
                                                                                            }
                                                                                        />
                                                                                    }
                                                                                    label={item.label}
                                                                                />
                                                                            );
                                                                        })}
                                                                    </RadioGroup>
                                                                    {errors.filmReleaseTypeID &&
                                                                        touched.filmReleaseTypeID && (
                                                                        <small
                                                                            className="error"
                                                                            style={{ left: 3 }}
                                                                        >
                                                                            {errors.filmReleaseTypeID}
                                                                        </small>
                                                                    )}
                                                                </FormControl>
                                                            );
                                                        }}
                                                    </Field>
                                                </Grid2>
                                            </Grid2>
                                            <Grid2 container size={12}>
                                                <Grid2
                                                    className="field-label-wrapper"
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}>
                                                    <Typography variant={"subtitle2"} className="field-label">
                                                        Company
                                                    </Typography>
                                                </Grid2>
                                                <Grid2
                                                    className="field"
                                                    size={{
                                                        xs: 12,
                                                        md: 8
                                                    }}>
                                                    <Field name="companyID">
                                                        {({ field, form: { touched, errors, setFieldValue } }) => {
                                                            return (
                                                                <React.Fragment>
                                                                    <CompanySelect
                                                                        onChange={(option) => {
                                                                            setFieldValue("companyID", option);
                                                                            setFieldValue("contacts", []);
                                                                        }}
                                                                        defaultValue={field.value}
                                                                        placeholder="Select Company"
                                                                    />
                                                                    {}
                                                                    {errors.companyID && touched.companyID && (
                                                                        <small className="error">
                                                                            {errors.companyID}
                                                                        </small>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        }}
                                                    </Field>
                                                </Grid2>
                                            </Grid2>
                                            <Grid2 container size={12}>
                                                <Grid2
                                                    className="field-label-wrapper"
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}>
                                                    <Typography variant={"subtitle2"} className="field-label">
                                                        Alternative titles
                                                    </Typography>
                                                </Grid2>
                                                <Grid2

                                                    size={{
                                                        xs: 12,
                                                        md: 8
                                                    }}>
                                                    <Field name="alternativeTitles">
                                                        {({ field, form: { touched, errors } }) => {
                                                            // eslint-disable-next-line no-unused-vars
                                                            const { onChange, ...restField } = field;
                                                            return (
                                                                <React.Fragment>
                                                                    <Select
                                                                        placeholder={" "}
                                                                        required={false}
                                                                        isClearable={false}
                                                                        multiple
                                                                        defaultMenuPortalTarget
                                                                        menuIsOpen={false}
                                                                        value={values.alternativeTitles}
                                                                        {...restField}
                                                                        options={[]}
                                                                        className={
                                                                            errors.alternativeTitles &&
                                                                            touched.alternativeTitles
                                                                                ? "error"
                                                                                : ""
                                                                        }
                                                                    />
                                                                    {errors.alternativeTitles &&
                                                                        touched.alternativeTitles && (
                                                                        <small className="error">
                                                                            {errors.alternativeTitles}
                                                                        </small>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        }}
                                                    </Field>
                                                </Grid2>
                                            </Grid2>
                                            <Grid2 container size={12}>
                                                <Grid2

                                                    className="field-label-wrapper"
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}>
                                                    <Typography variant={"subtitle2"} className="field-label">
                                                        Title Related Contacts
                                                    </Typography>
                                                </Grid2>
                                                <Grid2

                                                    style={{ paddingTop: 16, paddingBottom: 8 }}
                                                    size={{
                                                        xs: 12,
                                                        md: 10
                                                    }}>
                                                    <Field name="contacts">
                                                        {({
                                                            field,
                                                            form: { touched, errors, setFieldValue, values },
                                                        }) => {
                                                            return (
                                                                <React.Fragment>
                                                                    {}
                                                                    <ContactDropdown
                                                                        maxOptions={4}
                                                                        onChange={(option) =>
                                                                            setFieldValue("contacts", option)
                                                                        }
                                                                        value={field.value}
                                                                        readOnly={false}
                                                                        options={
                                                                            values.companyID &&
                                                                            values.companyID.contacts
                                                                                ? values.companyID.contacts
                                                                                : []
                                                                        }
                                                                    />

                                                                    {}
                                                                    {errors.advertisementType &&
                                                                        touched.advertisementType && (
                                                                        <small className="error">
                                                                            {errors.advertisementType}
                                                                        </small>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        }}
                                                    </Field>
                                                </Grid2>
                                            </Grid2>

                                            <Grid2 container size={12}>
                                                <Grid2

                                                    className="field-label-wrapper"
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}>
                                                    <Typography variant={"subtitle2"} className="field-label">
                                                        Rating
                                                    </Typography>
                                                </Grid2>
                                                <Grid2

                                                    style={{ paddingTop: 16, paddingBottom: 8 }}
                                                    size={{
                                                        xs: 12,
                                                        md: 10
                                                    }}>
                                                    <Field name="filmRatingTypeID">
                                                        {({ field, form: { touched, errors, setFieldValue } }) => {
                                                            // eslint-disable-next-line no-unused-vars
                                                            const { onChange, ...restField } = field;
                                                            return (
                                                                <React.Fragment>
                                                                    <FilmRatingSwitch
                                                                        onChange={(option) =>
                                                                            setFieldValue("filmRatingTypeID", option)
                                                                        }
                                                                        {...restField}
                                                                    />

                                                                    {errors.rating && touched.rating && (
                                                                        <small className="error">{errors.rating}</small>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        }}
                                                    </Field>
                                                </Grid2>
                                            </Grid2>
                                            <Grid2 container size={12}>
                                                <Grid2

                                                    className="field-label-wrapper"
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}>
                                                    <Typography variant={"subtitle2"} className="field-label">
                                                        Rating Category
                                                    </Typography>
                                                </Grid2>
                                                <Grid2

                                                    style={{ paddingTop: 16, paddingBottom: 8 }}
                                                    size={{
                                                        xs: 12,
                                                        md: 10
                                                    }}>
                                                    <RatingCategoryComponent
                                                        category={values.ratingCategory}
                                                        hasMargin
                                                        subCategory={values.ratingSubCategory}
                                                        onChange={handleCategoryChange}
                                                    />
                                                </Grid2>
                                            </Grid2>
                                            <Grid2 container size={12}>
                                                <Grid2

                                                    className="field-label-wrapper"
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}>
                                                    <Typography
                                                        variant={"subtitle2"}
                                                        className="field-label"
                                                        style={{ alignSelf: "unset", paddingTop: 15 }}
                                                    >
                                                        Reasons
                                                    </Typography>
                                                </Grid2>
                                                <Grid2

                                                    size={{
                                                        xs: 12,
                                                        md: 8
                                                    }}>
                                                    <Field name="ratingReason">
                                                        {({ field, form: { touched, errors } }) => {
                                                            return (
                                                                <React.Fragment>
                                                                    <TextField
                                                                        className="form-control"
                                                                        margin="normal"
                                                                        variant="outlined"
                                                                        multiline
                                                                        rows="3"
                                                                        {...field}
                                                                    />
                                                                    {errors.reasons && touched.reasons && (
                                                                        <small className="error">
                                                                            {errors.reasons}
                                                                        </small>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        }}
                                                    </Field>
                                                </Grid2>
                                            </Grid2>
                                            <Grid2 container size={12}>
                                                <Grid2

                                                    className="field-label-wrapper"
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}></Grid2>
                                                <Grid2
                                                    container

                                                    size={{
                                                        xs: 12,
                                                        md: 8
                                                    }}>
                                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                        <Grid2

                                                            size={{
                                                                xs: 12,
                                                                sm: 4,
                                                                md: 4
                                                            }}>
                                                            <Field name="releaseDate">
                                                                {({
                                                                    field,
                                                                    form: { touched, errors, setFieldValue },
                                                                }) => {
                                                                    log.debug("releaseDate", field.value);
                                                                    return (
                                                                        <React.Fragment>
                                                                            <Typography
                                                                                variant={"subtitle2"}
                                                                                className="date-label"
                                                                            >
                                                                                Release Date
                                                                            </Typography>
                                                                            <ResponsiveDatePicker
                                                                                style={{ marginTop: 0 }}
                                                                                inputVariant="outlined"
                                                                                variant="inline"
                                                                                margin="normal"
                                                                                value={field.value}
                                                                                autoOk={false}
                                                                                onChange={(date) =>
                                                                                    setFieldValue(
                                                                                        "releaseDate",
                                                                                        date
                                                                                    )
                                                                                }
                                                                                KeyboardButtonProps={{
                                                                                    "aria-label": "change date",
                                                                                }}
                                                                            />
                                                                            {errors.releaseDate && touched.releaseDate && (
                                                                                <small className="error">
                                                                                    {errors.releaseDate}
                                                                                </small>
                                                                            )}
                                                                        </React.Fragment>
                                                                    );
                                                                }}
                                                            </Field>
                                                        </Grid2>
                                                        <Grid2

                                                            size={{
                                                                sm: 4,
                                                                xs: 12,
                                                                md: 4
                                                            }}>
                                                            <Field name="ratingDate">
                                                                {({
                                                                    field,
                                                                    form: { touched, errors, setFieldValue },
                                                                }) => {
                                                                    log.debug("ratingDate", field.value);
                                                                    return (
                                                                        <React.Fragment>
                                                                            <Typography
                                                                                variant={"subtitle2"}
                                                                                className="date-label"
                                                                            >
                                                                                Certificate Date
                                                                            </Typography>
                                                                            <ResponsiveDatePicker
                                                                                style={{ marginTop: 0 }}
                                                                                inputVariant="outlined"
                                                                                variant="inline"
                                                                                margin="normal"
                                                                                value={field.value}
                                                                                onChange={(date) =>
                                                                                    setFieldValue(
                                                                                        "ratingDate",
                                                                                        date
                                                                                    )
                                                                                }
                                                                                KeyboardButtonProps={{
                                                                                    "aria-label": "change date",
                                                                                }}
                                                                            />
                                                                            {errors.ratingDate && touched.ratingDate && (
                                                                                <small className="error">
                                                                                    {errors.ratingDate}
                                                                                </small>
                                                                            )}
                                                                        </React.Fragment>
                                                                    );
                                                                }}
                                                            </Field>
                                                        </Grid2>
                                                        <Grid2

                                                            size={{
                                                                sm: 4,
                                                                xs: 12,
                                                                md: 4
                                                            }}>
                                                            <Field name="enteredDate">
                                                                {({ field, form: { touched, errors } }) => {
                                                                    log.debug("enteredDate", field.value);
                                                                    return (
                                                                        <React.Fragment>
                                                                            <Typography
                                                                                variant={"subtitle2"}
                                                                                className="date-label"
                                                                            >
                                                                                Date Entered
                                                                            </Typography>
                                                                            <ResponsiveDatePicker
                                                                                style={{ marginTop: 0 }}
                                                                                disabled
                                                                                inputVariant="outlined"
                                                                                variant="dialog"
                                                                                margin="normal"
                                                                                value={field.value}
                                                                                KeyboardButtonProps={{
                                                                                    "aria-label": "change date",
                                                                                }}
                                                                            />
                                                                            {errors.enteredDate && touched.enteredDate && (
                                                                                <small className="error">
                                                                                    {errors.enteredDate}
                                                                                </small>
                                                                            )}
                                                                        </React.Fragment>
                                                                    );
                                                                }}
                                                            </Field>
                                                        </Grid2>
                                                    </LocalizationProvider>
                                                </Grid2>
                                            </Grid2>
                                        </Grid2>
                                    </Grid2>
                                </Form>
                            </div>
                            {editing && (
                                <Grid2 style={{ padding: "10px 30px 30px", marginBottom: 80 }} container spacing={3}>
                                    <Grid2 container spacing={3} size={12}>
                                        <Grid2

                                            size={{
                                                xs: 12,
                                                md: 2
                                            }}>
                                            <Typography variant={"subtitle2"} className="field-2">
                                                Merge to
                                            </Typography>
                                        </Grid2>
                                        <Grid2

                                            size={{
                                                xs: 6,
                                                md: 8
                                            }}>
                                            <FilmReleaseSelect2
                                                key={"merge"}
                                                filmReleaseId={initValues && initValues.id ? initValues.id : null}
                                                defaultValue={releaseToMerge}
                                                onChange={onMergeTitle}
                                            />
                                        </Grid2>
                                        <Grid2
                                            size={{
                                                xs: 4,
                                                md: 2
                                            }}>
                                            <Button variant={"outlined"} disabled={!releaseToMerge} onClick={handleOnMerge}>
                                                {" "}
                                                Perform the merge
                                            </Button>
                                        </Grid2>
                                    </Grid2>

                                    <Grid2 container spacing={3} size={12}>
                                        <Grid2
                                            size={{
                                                xs: 12,
                                                md: 2
                                            }}>
                                            <Typography variant={"subtitle2"} className="field-2">
                                                Related to
                                            </Typography>
                                        </Grid2>
                                        <Grid2
                                            size={{
                                                xs: 6,
                                                md: 8
                                            }}>
                                            <FilmReleaseSelect2
                                                key={"associate"}
                                                filmReleaseId={initValues && initValues.id ? initValues.id : null}
                                                defaultValue={releaseToAssociate}
                                                onChange={onAssociateRelease}
                                            />
                                        </Grid2>
                                        <Grid2
                                            size={{
                                                xs: 4,
                                                md: 2
                                            }}>
                                            <Button variant={"outlined"} disabled={!releaseToAssociate} onClick={handleOnAssociate}>
                                                {" "}
                                                Associate release
                                            </Button>
                                        </Grid2>
                                    </Grid2>
                                </Grid2>
                            )}
                        </FormContainer>
                    </React.Fragment>)
                );
            }}
        </Formik>)
    );
}

function FormContainer({ children }) {
    const theme = useTheme();
    const isSmallDevice = useMediaQuery(theme.breakpoints.down(SMALL_DEVICE_MEDIA_SIZE));
    if (isSmallDevice) {
        return <React.Fragment>{children}</React.Fragment>;
    }
    return (
        <Container maxWidth="lg" style={{ paddingBottom: 200 }}>
            {children}
        </Container>
    );
}

export default forwardRef(FilmReleaseForm);
